import { Typography, Box, styled } from '@mui/material';

import { SnackbarKey, useSnackbar } from 'notistack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  SnackbarFeedback,
  SnackbarType,
  feedbackSnackbarMap,
} from '../services/feedbackManager';
import { ReactNode } from 'react';

const StyledErrorIcon = styled(ErrorOutlineIcon)({
  fontSize: 36,
  marginRight: 15,
});
const StyledSuccessIcon = styled(CheckCircleOutlineIcon)({
  fontSize: 36,
  marginRight: 15,
});
const StyledWarning = styled(WarningAmberIcon)({
  fontSize: 36,
  marginRight: 15,
});

const StyledContiner = styled(Box)<{ status: SnackbarType }>(
  ({ theme, status }) => ({
    width: '100vw',
    color: theme.palette[status].dark,
    backgroundColor: theme.palette[status].light,
    borderTop: `3px solid ${theme.palette[status].dark}`,
    textAlign: 'center',
    padding: theme.spacing(1),
  }),
);

const StyledTypography = styled(Typography)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

export const feedbackIconMap: Record<SnackbarType, ReactNode> = {
  error: <StyledErrorIcon />,
  success: <StyledSuccessIcon />,
  warning: <StyledWarning />,
};

const useFeedbackSnackbar = (): [
  ({ type, isPersistent }: SnackbarFeedback) => void,
  (key?: SnackbarKey | undefined) => void,
] => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  return [
    async ({ type, isPersistent = false }) => {
      const {
        message,
        status,
        disableWindowBlurListener = false,
      } = feedbackSnackbarMap[type];
      enqueueSnackbar(message, {
        autoHideDuration: feedbackSnackbarMap[type]?.hideDuration ?? 3000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        persist: isPersistent,
        preventDuplicate: true,
        disableWindowBlurListener,

        content: (key, message) => (
          <StyledContiner
            key={key}
            status={status}
            data-testid="feedback-snackbar"
          >
            <StyledTypography variant="body3U">
              {!!feedbackSnackbarMap[type].customIcon
                ? feedbackSnackbarMap[type].customIcon
                : feedbackIconMap[status]}
              {message}
            </StyledTypography>
          </StyledContiner>
        ),
      });
    },
    closeSnackbar,
  ];
};

export default useFeedbackSnackbar;
